import type { ElementType, ReactNode } from 'react';

import { AutoAwesome, SparkleIcon } from 'components/icons';
import { cn } from 'utils/cn';

/**
 * Each size's own drawing: the AI home's (`page`), the panel's and the phone's (`compact`), and the Welcome dialog's.
 * The glows and stars are placed for the width each one is drawn at.
 */
const SIZES = {
    page: {
        hero: 'min-h-[144px] gap-2 rounded-[24px] px-8 pt-6 pb-5',
        blobs: [
            'left-[-60px] top-[-110px] h-[220px] w-[300px] bg-welcome-star opacity-20 [--welcome-loop:16s]',
            'right-[-70px] top-[-90px] h-48 w-[280px] bg-secondary opacity-34 [--welcome-loop:19s] [--welcome-dir:alternate-reverse]',
            'right-[140px] bottom-[-150px] h-48 w-[300px] bg-secondary opacity-32 [--welcome-loop:22s]',
            'left-[180px] bottom-[-160px] h-48 w-80 bg-welcome-star opacity-16 [--welcome-loop:18s] [--welcome-dir:alternate-reverse]',
        ],
        stars: [
            'left-16 top-6 size-2.5 text-welcome-hero-ink',
            'left-[560px] top-[22px] size-[9px] text-welcome-hero-ink [--welcome-delay:0.6s]',
            'left-[628px] top-[84px] size-3 text-secondary [--welcome-delay:1.8s]',
            'left-[470px] top-[100px] size-1.5 text-welcome-star [--welcome-delay:2.4s]',
            'left-[300px] top-3.5 size-1.5 text-welcome-star [--welcome-delay:1.5s]',
            'left-[690px] top-11 size-[7px] text-welcome-hero-ink [--welcome-delay:2.9s]',
        ],
        scrim: 'h-[120px]',
        row: 'gap-3.5',
        mark: 'size-12 rounded-[14px]',
        glyph: 'size-6',
        text: 'gap-1',
        greeting: 'text-h2 leading-[1.15] tracking-[-0.02em]',
        line: 'text-section font-normal text-pretty',
    },
    compact: {
        hero: 'min-h-[132px] gap-1.5 rounded-[24px] px-5 pt-5 pb-4',
        blobs: [
            'left-[-60px] top-[-100px] h-[180px] w-[220px] bg-welcome-star opacity-20 [--welcome-loop:16s]',
            'right-[-70px] top-[-80px] h-40 w-[220px] bg-secondary opacity-34 [--welcome-loop:19s] [--welcome-dir:alternate-reverse]',
            'right-[60px] bottom-[-130px] h-40 w-[220px] bg-secondary opacity-32 [--welcome-loop:22s]',
            'left-[100px] bottom-[-140px] h-40 w-60 bg-welcome-star opacity-16 [--welcome-loop:18s] [--welcome-dir:alternate-reverse]',
        ],
        stars: [
            'left-[10%] top-5 size-2.5 text-welcome-hero-ink',
            'left-[47%] top-3 size-1.5 text-welcome-star [--welcome-delay:1.5s]',
            'left-[70%] top-4 size-[9px] text-welcome-hero-ink [--welcome-delay:0.6s]',
            'left-[84%] top-[68px] size-3 text-secondary [--welcome-delay:1.8s]',
            'left-[92%] top-9 size-[7px] text-welcome-hero-ink [--welcome-delay:2.9s]',
        ],
        scrim: 'h-[110px]',
        row: 'gap-3',
        mark: 'size-10 rounded-[12px]',
        glyph: 'size-5',
        text: 'gap-1',
        greeting: 'text-page-title',
        line: 'text-body1 text-pretty',
    },
    // Under 768px the Welcome fills the screen: its text takes the hero's whole width from the mark down, and only
    // the stars above it show.
    welcome: {
        hero: 'min-h-[212px] shrink-0 gap-2.5 px-10 pt-[34px] pb-7 max-sm:min-h-0 max-sm:px-5 max-sm:pt-[52px] max-sm:pb-5',
        blobs: [
            'left-[-60px] top-[-90px] h-[260px] w-[300px] bg-welcome-star opacity-20 [--welcome-loop:16s]',
            'right-[-70px] top-[-70px] h-60 w-[280px] bg-secondary opacity-34 [--welcome-loop:19s] [--welcome-dir:alternate-reverse]',
            'right-[140px] bottom-[-150px] h-60 w-[300px] bg-secondary opacity-32 [--welcome-loop:22s]',
            'left-[180px] bottom-[-170px] h-60 w-80 bg-welcome-star opacity-16 [--welcome-loop:18s] [--welcome-dir:alternate-reverse]',
        ],
        stars: [
            'left-16 top-10 size-2.5 text-welcome-hero-ink',
            'left-[150px] top-[150px] size-[7px] text-secondary [--welcome-delay:1.1s] max-sm:hidden',
            'left-[560px] top-[34px] size-[9px] text-welcome-hero-ink [--welcome-delay:0.6s]',
            'left-[628px] top-32 size-3 text-secondary [--welcome-delay:1.8s] max-sm:hidden',
            'left-[470px] top-40 size-1.5 text-welcome-star [--welcome-delay:2.4s] max-sm:hidden',
            'left-[300px] top-[22px] size-1.5 text-welcome-star [--welcome-delay:1.5s]',
            'left-[690px] top-[60px] size-[7px] text-welcome-hero-ink [--welcome-delay:2.9s]',
        ],
        scrim: 'h-[180px]',
        row: 'gap-[18px] max-sm:flex-col max-sm:items-start max-sm:gap-3.5',
        mark: 'size-16 rounded-[18px] max-sm:size-12 max-sm:rounded-[14px]',
        glyph: 'size-8 max-sm:size-6',
        text: 'gap-1.5 max-sm:gap-1',
        greeting:
            'text-h2 max-sm:text-page-title leading-[1.15] font-semibold tracking-[-0.02em] max-sm:leading-[1.2] max-sm:tracking-[-0.015em]',
        line: 'text-section max-sm:text-body1 font-normal',
    },
};

export type AiHeroSize = keyof typeof SIZES;

type Props = {
    size: AiHeroSize;
    /** The small uppercase line over the greeting. */
    eyebrow: ReactNode;
    greeting: ReactNode;
    line?: ReactNode;
    /** What renders the greeting, an `h2` unless given: the Welcome passes its dialog's title. */
    greetingAs?: ElementType<{ className?: string; children?: ReactNode }>;
    /** What renders the line, a `p` unless given: the Welcome passes its dialog's description. */
    lineAs?: ElementType<{ className?: string; children?: ReactNode }>;
    /** Classes for the mark-and-greeting row and for the line, such as the Welcome's staged entrance. */
    rowClassName?: string;
    lineClassName?: string;
    /** Classes for the mark, and what it holds beside the glyph: the Welcome's one-time ring and burst. */
    markClassName?: string;
    markChildren?: ReactNode;
    className?: string;
    /** Laid over the hero, such as a close button. */
    children?: ReactNode;
};

/**
 * The Crespect greeting hero: the new design's Welcome hero, deep green with light text, its brand glows drifting and
 * its stars twinkling for as long as it shows (`welcome-blob` and `welcome-star` in `index.css`, each with its own
 * loop, direction or delay; reduced motion stops them). It carries `data-design="new"`, as the Welcome tokens exist
 * only in the new design, so it draws the same in classic; the tokens keep it dark green in the dark theme.
 *
 * A scrim lies over the glows and stars, under the text: a glow drifting behind a line would wash it out. Its height
 * is what darkens the eyebrow, the top line, enough for 4.5:1.
 */
export function AiHero({
    size,
    eyebrow,
    greeting,
    line,
    greetingAs: Greeting = 'h2',
    lineAs: Line = 'p',
    rowClassName,
    lineClassName,
    markClassName,
    markChildren,
    className,
    children,
}: Props) {
    const drawing = SIZES[size];
    return (
        <div
            data-design="new"
            className={cn(
                'text-welcome-hero-ink relative isolate flex flex-col justify-end overflow-hidden bg-[radial-gradient(120%_140%_at_20%_0%_in_oklab,var(--welcome-hero-from),var(--welcome-hero-to))]',
                drawing.hero,
                className
            )}
        >
            {drawing.blobs.map((blob) => (
                <span key={blob} aria-hidden className={cn('welcome-blob absolute rounded-full blur-[42px]', blob)} />
            ))}
            {drawing.stars.map((star) => (
                <SparkleIcon
                    key={star}
                    fill="currentColor"
                    stroke="none"
                    className={cn('welcome-star absolute', star)}
                />
            ))}
            <div
                aria-hidden
                className={cn(
                    'pointer-events-none absolute inset-x-0 bottom-0 bg-linear-to-t from-(--welcome-hero-to) to-transparent',
                    drawing.scrim
                )}
            />
            {children}
            <div className={cn('relative flex items-center', drawing.row, rowClassName)}>
                <span
                    className={cn(
                        'border-welcome-hero-ink/28 bg-welcome-hero-ink/12 text-secondary relative flex shrink-0 items-center justify-center border',
                        drawing.mark,
                        markClassName
                    )}
                >
                    <AutoAwesome className={drawing.glyph} />
                    {markChildren}
                </span>
                <div className={cn('flex min-w-0 flex-col', drawing.text)}>
                    <span className="text-label text-secondary font-semibold uppercase">{eyebrow}</span>
                    <Greeting className={cn('text-balance', drawing.greeting)}>{greeting}</Greeting>
                </div>
            </div>
            {line ? (
                <Line className={cn('text-welcome-hero-muted relative', drawing.line, lineClassName)}>{line}</Line>
            ) : null}
        </div>
    );
}
